"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useBoostedDrops } from "@/hooks/useBoostedDrops";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import BoostedDropCardHome from "./BoostedDropCardHome";
const BOOSTED_DROPS_LIMIT = 50;
export function BoostedSection() {
  const $ = _c(31);
  const router = useRouter();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      limit: BOOSTED_DROPS_LIMIT
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: drops,
    isLoading
  } = useBoostedDrops(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const container = scrollRef.current;
      if (!container) {
        return;
      }
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      setCanScrollLeft(container.scrollLeft > 1);
      setCanScrollRight(container.scrollLeft < maxScrollLeft - 1);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateScrollState = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const container_0 = scrollRef.current;
      if (!container_0) {
        return;
      }
      updateScrollState();
      const handleScroll = () => updateScrollState();
      container_0.addEventListener("scroll", handleScroll, {
        passive: true
      });
      window.addEventListener("resize", updateScrollState);
      return () => {
        container_0.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", updateScrollState);
      };
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = drops?.length;
  let t4;
  if ($[3] !== t3) {
    t4 = [updateScrollState, t3];
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(t2, t4);
  let t5;
  if ($[5] !== router) {
    t5 = drop => {
      router.push(`/waves?wave=${drop.wave.id}&serialNo=${drop.serial_no}`);
    };
    $[5] = router;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleDropClick = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = direction => {
      if (!scrollRef.current) {
        return;
      }
      scrollRef.current.scrollBy({
        left: direction === "left" ? -260 : 260,
        behavior: "smooth"
      });
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const scroll = t6;
  if (isLoading) {
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <section className="-tw-mx-8 tw-border-x-0 tw-border-y tw-border-solid tw-border-iron-900 tw-bg-iron-950 tw-px-4 tw-py-16 md:tw-px-6 lg:tw-px-8"><div className="tw-px-8"><div className="tw-flex tw-h-64 tw-items-center tw-justify-center"><div className="tw-text-sm tw-text-iron-500">Loading...</div></div></div></section>;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    return t7;
  }
  if (!drops || drops.length === 0) {
    return null;
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div><span className="tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-white md:tw-text-2xl">Boosted Drops</span><p className="tw-mb-0 tw-mt-2 tw-text-base tw-text-iron-500">Community-boosted right now</p></div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => scroll("left");
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = !canScrollLeft;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ChevronLeftIcon className="tw-size-4 tw-flex-shrink-0" />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t9) {
    t11 = <button type="button" onClick={t8} disabled={t9} className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-iron-900 tw-text-iron-500 tw-shadow-lg tw-ring-1 tw-ring-iron-50/5 tw-transition-all hover:tw-scale-105 hover:tw-bg-iron-800 hover:tw-text-iron-50 active:tw-scale-95 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 disabled:hover:tw-scale-100 disabled:hover:tw-bg-iron-900 disabled:hover:tw-text-iron-500" aria-label="Scroll left">{t10}</button>;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => scroll("right");
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = !canScrollRight;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronRightIcon className="tw-size-4" />;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t13) {
    t15 = <button type="button" onClick={t12} disabled={t13} className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-iron-900 tw-text-iron-500 tw-shadow-lg tw-ring-1 tw-ring-iron-50/5 tw-transition-all hover:tw-scale-105 hover:tw-bg-iron-800 hover:tw-text-iron-50 active:tw-scale-95 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 disabled:hover:tw-scale-100 disabled:hover:tw-bg-iron-900 disabled:hover:tw-text-iron-500" aria-label="Scroll right">{t14}</button>;
    $[16] = t13;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t11 || $[19] !== t15) {
    t16 = <div className="tw-mb-8 tw-flex tw-flex-col tw-items-start tw-gap-4 sm:tw-flex-row sm:tw-items-end sm:tw-justify-between">{t7}<div className="tw-flex tw-items-center tw-gap-2">{t11}{t15}</div></div>;
    $[18] = t11;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== drops || $[22] !== handleDropClick) {
    let t18;
    if ($[24] !== handleDropClick) {
      t18 = drop_0 => <BoostedDropCardHome key={drop_0.id} drop={drop_0} onClick={() => handleDropClick(drop_0)} />;
      $[24] = handleDropClick;
      $[25] = t18;
    } else {
      t18 = $[25];
    }
    t17 = drops.map(t18);
    $[21] = drops;
    $[22] = handleDropClick;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[26] !== t17) {
    t18 = <div ref={scrollRef} className="-tw-mx-6 tw-flex tw-gap-5 tw-overflow-x-auto tw-scroll-smooth tw-px-6 tw-pb-8 tw-pt-4 tw-scrollbar-none md:-tw-mx-8 md:tw-px-8">{t17}</div>;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t16 || $[29] !== t18) {
    t19 = <section className="-tw-mx-8 tw-border-x-0 tw-border-y tw-border-solid tw-border-iron-900 tw-bg-iron-950 tw-px-4 tw-py-10 md:tw-py-16 md:tw-px-6 lg:tw-px-8"><div className="tw-px-8">{t16}{t18}</div></section>;
    $[28] = t16;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  return t19;
}